import React from 'react';
import Icon from '../../../components/AppIcon';
import SettingsSection from './SettingsSection';
import SettingItem from './SettingItem';
import ToggleSwitch from './ToggleSwitch';

/**
 * Enhanced privacy tab component for VA profile settings
 * Uses main profile data and saves through central save function
 */
const VAPrivacyTab = ({ profileData, onInputChange }) => {
  // Get privacy settings from main profile data or use defaults
  const privacySettings = profileData.privacy_settings || {
    profile_visible: true,
    contact_info_visible: false,
    experience_visible: true,
    skills_visible: true,
    portfolio_visible: true,
    hourly_rate_visible: true,
    availability_visible: true,
    education_visible: true,
    certifications_visible: true,
    languages_visible: true,
    allow_direct_messages: true,
    show_online_status: false,
    allow_profile_indexing: true,
    public_portfolio: false,
    hide_from_search: false,
    anonymous_browsing: false
  };
  
  // Handle privacy setting changes
  const handleToggleChange = (setting, value) => {
    const newSettings = {
      ...privacySettings,
      [setting]: value
    };
    
    // Update main profile data
    onInputChange('privacy_settings', newSettings);
  };

  return (
    <div className="space-y-6">
      {/* Profile Visibility */}
      <SettingsSection
        title="Profile Visibility"
        description="Control what information is visible to employers and other users"
        icon="Eye"
      >
        <div className="space-y-4">
          <SettingItem
            title="Make Profile Public"
            description="Allow your profile to be discovered by employers and clients"
            control={
              <ToggleSwitch
                checked={privacySettings.profile_visible}
                onChange={(checked) => handleToggleChange('profile_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Contact Information"
            description="Display your phone number and email to verified employers"
            control={
              <ToggleSwitch
                checked={privacySettings.contact_info_visible}
                onChange={(checked) => handleToggleChange('contact_info_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Work Experience"
            description="Display your work history and past projects"
            control={
              <ToggleSwitch
                checked={privacySettings.experience_visible}
                onChange={(checked) => handleToggleChange('experience_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Skills & Expertise"
            description="Display your skills, certifications, and expertise areas"
            control={
              <ToggleSwitch
                checked={privacySettings.skills_visible}
                onChange={(checked) => handleToggleChange('skills_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Portfolio"
            description="Display your work samples and portfolio items"
            control={
              <ToggleSwitch
                checked={privacySettings.portfolio_visible}
                onChange={(checked) => handleToggleChange('portfolio_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Hourly Rate"
            description="Display your pricing information to potential clients"
            control={
              <ToggleSwitch
                checked={privacySettings.hourly_rate_visible}
                onChange={(checked) => handleToggleChange('hourly_rate_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Availability Status"
            description="Let employers see if you're currently available for new projects"
            control={
              <ToggleSwitch
                checked={privacySettings.availability_visible}
                onChange={(checked) => handleToggleChange('availability_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Education"
            description="Display your educational background and qualifications"
            control={
              <ToggleSwitch
                checked={privacySettings.education_visible}
                onChange={(checked) => handleToggleChange('education_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Certifications"
            description="Display your professional certifications and achievements"
            control={
              <ToggleSwitch
                checked={privacySettings.certifications_visible}
                onChange={(checked) => handleToggleChange('certifications_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Languages"
            description="Display the languages you speak and your proficiency levels"
            control={
              <ToggleSwitch
                checked={privacySettings.languages_visible}
                onChange={(checked) => handleToggleChange('languages_visible', checked)}
              />
            }
          />
        </div>
      </SettingsSection>

      {/* Communication Settings */}
      <SettingsSection
        title="Communication & Contact"
        description="Manage how others can contact and interact with you"
        icon="MessageCircle"
      >
        <div className="space-y-4">
          <SettingItem
            title="Allow Direct Messages"
            description="Let employers and clients send you direct messages"
            control={
              <ToggleSwitch
                checked={privacySettings.allow_direct_messages}
                onChange={(checked) => handleToggleChange('allow_direct_messages', checked)}
              />
            }
          />

          <SettingItem
            title="Show Online Status"
            description="Display when you're currently online and active"
            control={
              <ToggleSwitch
                checked={privacySettings.show_online_status}
                onChange={(checked) => handleToggleChange('show_online_status', checked)}
              />
            }
          />
        </div>
      </SettingsSection>

      {/* Search & Discovery */}
      <SettingsSection
        title="Search & Discovery"
        description="Control how your profile appears in search results and recommendations"
        icon="Search"
      >
        <div className="space-y-4">
          <SettingItem
            title="Allow Search Engine Indexing"
            description="Let search engines like Google index your public profile"
            control={
              <ToggleSwitch
                checked={privacySettings.allow_profile_indexing}
                onChange={(checked) => handleToggleChange('allow_profile_indexing', checked)}
              />
            }
          />

          <SettingItem
            title="Public Portfolio Access"
            description="Allow anyone with the link to view your portfolio without signing up"
            control={
              <ToggleSwitch
                checked={privacySettings.public_portfolio}
                onChange={(checked) => handleToggleChange('public_portfolio', checked)}
              />
            }
          />

          <SettingItem
            title="Hide from Internal Search"
            description="Don't show your profile in VA Sprouts internal search results"
            control={
              <ToggleSwitch
                checked={privacySettings.hide_from_search}
                onChange={(checked) => handleToggleChange('hide_from_search', checked)}
              />
            }
          />

          <SettingItem
            title="Anonymous Profile Browsing"
            description="Don't show your name when viewing other profiles (shows as 'Anonymous User')"
            control={
              <ToggleSwitch
                checked={privacySettings.anonymous_browsing}
                onChange={(checked) => handleToggleChange('anonymous_browsing', checked)}
              />
            }
          />
        </div>
      </SettingsSection>

      {/* Privacy Notice */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h4 className="font-medium text-text-primary">Privacy & Data Protection</h4>
            <div className="text-sm text-text-secondary space-y-2">
              <p>
                Your privacy is important to us. We follow strict data protection guidelines and never share your personal information without your consent.
              </p>
              <ul className="list-disc list-inside space-y-1 pl-4">
                <li>Profile visibility settings apply immediately</li>
                <li>Contact information is only shared with verified employers</li>
                <li>You can update these settings anytime</li>
                <li>Some information may still be visible to platform administrators for safety</li>
                <li>Hiding your profile from search may reduce job opportunities</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VAPrivacyTab;